import React, { useCallback, useContext, useEffect, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import classnames from 'classnames';
import { Link } from 'gatsby';
import { useIntl } from 'react-intl';
import Header from 'components/header/header';
import Footer from 'components/footer/footer';
import Cookie from 'components/cookie/cookie';
import Notification from 'components/notification/notification';
import EmergencyMessage from 'components/emergencyMessage/EmergencyMessage';
import { ActionType, PageType, Language, StorageCookie } from 'enums/types';
import icon from 'components/image/headerIcon';
import { StateContext } from 'globalState';
import { setRefTracking, setUTM } from 'helpers/helpers';
import { DataParameters } from 'enums/DataParametersForUtm';
import { routes } from 'languages';
import useIsMobile from 'hooks/useIsMobile';
import * as style from './template.module.scss';

interface Props {
	children: JSX.Element | JSX.Element[];
	originalPath: string;
	pageType: PageType;
}

enum Direction {
	RTL = 'RTL',
	LTR = 'LTR',
}

interface UpdateSchedulerResponse {
	active: boolean;
	data?: {
		message: string;
		disableLoginButton: boolean;
	}
}

enum UpdateScheduleCacheKeys {
	LAST_ACCESS = 'updateMessageLastAccess',
	MESSAGE = 'updateMessage',
	DISABLE_LOGIN = 'updateMessageDisableLogin',
}

const UPDATE_SCHEDULER_REMOTE = '/update-scheduler-api/status';
// 5 minutes in milliseconds
const UPDATE_SCHEDULER_REVALIDATION_TIME = 5 * 60 * 1000;
const SHOULD_FETCH_UPDATE_SCHEDULER = process.env.GATSBY_APP !== 'local';

const blacklistedPathsForCookie = ['/loans-application-form/', '/privacy-policy-page/', '/swift-deposit-instructions/'];
const blacklistedPathsForHeaderAndFooter = [
	'/loans-application-form/',
	'/open-business-account/',
	'/privacy-policy-page/'
];
const blacklistedPathsForHeaderAndFooterMobile = ['/swift-deposit-instructions/'];
const blacklistedPathsForGeneralUTM = [
	'/open-business-account/',
	'/privacy-policy-page/',
	'/swift-deposit-instructions/',
];
const blacklistedPathsForNoNavigationLogo = ['/privacy-policy-page/', '/swift-deposit-instructions/'];

const logUpdateSchedulerError = (error: unknown) => {
	if (process.env.NODE_ENV !== 'production') {
		// eslint-disable-next-line no-console
		console.error(error);
	}
};

const Layout = ({ children, originalPath, pageType }: Props) => {
	const { locale } = useIntl();
	const { store, dispatch } = useContext(StateContext);
	const { emergencyMessage } = store;
	const isMobile = useIsMobile(576);
	const [showNotification, setIsShownNotification] = useState(false);
	const windowIsDefined = typeof window === 'object';
	const documentIsDefined = typeof document === 'object';
	const localStorageInstance = windowIsDefined ? window.localStorage : null;
	const userCountryCodeByLocation = localStorageInstance?.getItem(StorageCookie.DefaultLanguage) ?? null;

	const applyEmergencyMessage = useCallback((message: string, isLoginDisabled: boolean) => {
		dispatch({
			type: ActionType.SetEmergencyMessage,
			payload: {
				emergencyMessage: message,
				isLoginDisabled,
			},
		});
	}, [dispatch]);

	const updateMessageCheck = useCallback(() => {
		if (!SHOULD_FETCH_UPDATE_SCHEDULER) {
			return;
		}

		// Check if an update message is available from remote or session storage
		const lastAccess = localStorage.getItem(UpdateScheduleCacheKeys.LAST_ACCESS);
		if (lastAccess && Date.now() - parseInt(lastAccess, 10) < UPDATE_SCHEDULER_REVALIDATION_TIME) {
			const message = localStorage.getItem(UpdateScheduleCacheKeys.MESSAGE);
			const disableLoginButton = localStorage.getItem(UpdateScheduleCacheKeys.DISABLE_LOGIN);
			if (!message || !disableLoginButton) {
				return;
			}
			applyEmergencyMessage(message, !!parseInt(disableLoginButton, 10));
			return;
		}

		fetch(UPDATE_SCHEDULER_REMOTE, {
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json',
			},
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error('Could not connect to the server');
				}
				return response.json();
			})
			.then((response: UpdateSchedulerResponse) => {
				if (!response.active || !response.data) {
					localStorage.setItem(UpdateScheduleCacheKeys.LAST_ACCESS, Date.now().toString());
					localStorage.removeItem(UpdateScheduleCacheKeys.MESSAGE);
					localStorage.removeItem(UpdateScheduleCacheKeys.DISABLE_LOGIN);
					return;
				}
				const { message, disableLoginButton } = response.data;
				applyEmergencyMessage(message, disableLoginButton);
				localStorage.setItem(UpdateScheduleCacheKeys.LAST_ACCESS, Date.now().toString());
				localStorage.setItem(UpdateScheduleCacheKeys.MESSAGE, message);
				localStorage.setItem(UpdateScheduleCacheKeys.DISABLE_LOGIN, disableLoginButton ? '1' : '0');
			})
			.catch((error) => {
				logUpdateSchedulerError(error);
			});
	}, [applyEmergencyMessage]);

	useEffect(() => {
		if (documentIsDefined && document.referrer.length > 0) {
			localStorageInstance?.setItem(DataParameters.REFERRER, document.referrer);
		}

		const anchorHandlers: Array<{ element: Element; handler: (event: Event) => void }> = [];

		if (windowIsDefined && documentIsDefined) {
			smoothscroll.polyfill();
			updateMessageCheck();

			if (window.location.hash) {
				const element = document.getElementById(window.location.hash.replace('#', ''));
				if (element) {
					setTimeout(() => {
						window.scrollTo({
							behavior: 'smooth',
							top: element.offsetTop,
						});
					}, 100);
				}
			}

			const anchorLinks = Array.from(document.querySelectorAll('a[href^="#"]'));
			anchorLinks.forEach((item) => {
				const handler = (event: Event) => {
					event.preventDefault();
					const hashTag = (event.currentTarget as HTMLElement).getAttribute('href');
					const target = hashTag ? document.querySelector(hashTag) : null;
					window.scrollTo({
						behavior: 'smooth',
						top: target ? target.offsetTop : 60,
					});
				};
				item.addEventListener('click', handler);
				anchorHandlers.push({ element: item, handler });
			});
		}

		if (userCountryCodeByLocation === 'US') {
			setIsShownNotification(true);
		}

		const search = windowIsDefined ? window.location.search : '';

		if (
			search.includes(DataParameters.UTM_SOURCE) &&
			!blacklistedPathsForGeneralUTM.includes(originalPath)
		) {
			setUTM();
		}

		if (search.includes(DataParameters.REF)) {
			setRefTracking();
		}

		return () => {
			anchorHandlers.forEach(({ element, handler }) => {
				element.removeEventListener('click', handler);
			});
		};
	}, [documentIsDefined, localStorageInstance, originalPath, updateMessageCheck, userCountryCodeByLocation, windowIsDefined]);

	const dir = locale === Language.Arabic ? Direction.RTL : Direction.LTR;
	const hideHeaderFooter = blacklistedPathsForHeaderAndFooter.includes(originalPath)
		|| (blacklistedPathsForHeaderAndFooterMobile.includes(originalPath) && isMobile);

	return (
		<React.Fragment>
		{emergencyMessage && (
			<aside>
			<EmergencyMessage message={emergencyMessage} />
			</aside>
		)}

		<div className={style.container} dir={dir}>
			{!hideHeaderFooter && (
			<Header showLanguageSwitcher originalPath={originalPath} pageType={pageType} />
			)}

			{/* MAIN landmark: contains logo (when no header/footer) and page content */}
			<main id="main-content" role="main">
			{hideHeaderFooter && !blacklistedPathsForNoNavigationLogo.includes(originalPath) && (
				<div className={classnames(style.logoWrapper)}>
				<Link to={routes[locale]['/']} className={style.logo}>
					{icon.logo}
				</Link>
				</div>
			)}

			{children}
			</main>

			{!hideHeaderFooter && <Footer />}

			{showNotification && (
			<aside>
				<Notification showNotification={(value: boolean) => setIsShownNotification(value)} />
			</aside>
			)}

			{!blacklistedPathsForCookie.includes(originalPath) && (
			<aside>
				<Cookie />
			</aside>
			)}
		</div>
		</React.Fragment>
	);
	};

export default Layout;
